import { isString } from 'fitify-types/src/type-guards/common'
import { useAsyncRunOnce, useRunOnce } from 'fitify-ui/src/hooks/useRunOnce'
import { fetchDetectLocation } from 'fitify-ui-onboarding/src/api/nextApi'
import { usePromoContext } from 'fitify-ui-onboarding/src/contexts/PromoContext'
import { useBeforeLeaveDialog } from 'fitify-ui-onboarding/src/hooks/useBeforeLeaveDialog'
import { useRouter } from 'fitify-ui-onboarding/src/hooks/useRouter'
import { useGrowthBook } from 'fitify-ui-onboarding/src/utils/growthbook/generated'
import { useState } from 'react'

import { FetchDiscountForDiscountReveal } from 'components/App/SideEffects/FetchDiscountForDiscountReveal'
import { FETCH_DISCOUNT_PATHS } from 'components/App/SideEffects/FetchDiscountForDiscountReveal.Utils'
import { DEFAULT_PROMO_CODE } from 'data/promo'
import { DETECT_LOCATION_PAGES, NO_LEAVE_DIALOG_PAGES } from 'data/routes'
import { DID_YOU_KNOW_PLACEMENTS, type DidYouKnowPlacement } from 'types/state'
import { useDataState } from 'utils/state'

const isDidYouKnowPlacement = (
  value: unknown
): value is DidYouKnowPlacement => {
  return (
    isString(value) &&
    (DID_YOU_KNOW_PLACEMENTS as readonly string[]).includes(value)
  )
}

/**
 * Pages side effects such as fetching user location, leave page dialog, etc.
 * A component is used instead of a hook to be able to conditionally execute some side effects.
 */
export const PagesSideEffects = () => {
  const router = useRouter()
  const { data, set } = useDataState()
  const { promoCode } = usePromoContext()
  const growthBook = useGrowthBook()
  const [shouldFetchDiscountReveal, setShouldFetchDiscountReveal] =
    useState(false)

  useRunOnce(
    () => {
      const placement = growthBook.evalFeature(
        'onboarding_did_you_know_steps_placement'
      )?.value

      set('common', {
        didYouKnowPlacement: isDidYouKnowPlacement(placement)
          ? placement
          : 'none',
      })
    },
    growthBook.ready &&
      data.common.didYouKnowPlacement === undefined &&
      router.resolvedPathname.endsWith('/motivation-early')
  )

  // Fetch user location
  useAsyncRunOnce(
    async () => {
      const location = await fetchDetectLocation()
      set('common', {
        user_location: {
          isLoaded: true,
          data: location || undefined,
        },
      })
    },
    !data?.common?.user_location?.isLoaded &&
      DETECT_LOCATION_PAGES.some(
        (page) => page.path === router.resolvedPathname
      )
  )

  useBeforeLeaveDialog(
    NO_LEAVE_DIALOG_PAGES.every(
      (route) => route.path !== router.resolvedPathname
    )
  )

  // Check discount reveal enabled and set it to state
  useRunOnce(
    () => {
      setShouldFetchDiscountReveal(
        !promoCode || promoCode === DEFAULT_PROMO_CODE
      )
    },
    FETCH_DISCOUNT_PATHS.some((path) => path === router.resolvedPathname)
  )

  if (shouldFetchDiscountReveal) {
    return <FetchDiscountForDiscountReveal />
  }
  return null
}
